import { DEFAULT_LANGUAGE, DEFAULT_LANGUAGE_LONG, URL } from '../../../config/config';
import { GET_LANGUAGES } from '../../queries/Language';
import { Instance, flow, getParent, types } from 'mobx-state-tree';
import { LangModel } from './Lang';
import { captureException } from '../../../utils/sentry/sentry';
import { client } from '../../../config/apollo/client';

const en = require('./en.json');
const fr = require('./fr.json');
const nl = require('./nl.json');


const getLocaleUrl = lang => `${URL}${lang}/locale`;


export const LanguageStore = types
  .model('LanguageStore', {
    languages: types.optional(types.array(LangModel), () => [{ _id: '1', name: DEFAULT_LANGUAGE_LONG, key: DEFAULT_LANGUAGE }]),
    language: types.optional(types.string, DEFAULT_LANGUAGE),
    languageRoot: types.optional(types.string, DEFAULT_LANGUAGE),
    languagePhone: types.maybe(types.string),
    languageWPO: types.maybe(types.string),
    languageData: types.maybe(types.string),
    state: types.maybe(types.enumeration(['pending', 'done', 'error'])),
    ready: types.boolean
  })
  .actions(self => {
    const fetchLanguage = flow(function* () {
      self.state = 'pending';
      try {
        const data = yield fetch(getLocaleUrl(self.language), {
          method: 'GET',

        });

        const res = yield data.json();
        self.languageData = JSON.stringify(res.locale.data);
        self.state = 'done';
      } catch (error) {
        captureException(error);
        switch (self.language) {
        case 'en':
          self.languageData = yield Promise.resolve(JSON.stringify(en));
          break;
        case 'fr':
          self.languageData = yield Promise.resolve(JSON.stringify(fr));
          break;
        case 'nl':
          self.languageData = yield Promise.resolve(JSON.stringify(nl));
          break;
        default:
          self.languageData = yield Promise.resolve(JSON.stringify(eval(DEFAULT_LANGUAGE)));
          break;
        }
        self.state = 'done';
      }
      finally {
        self.ready= true;
      }
      return self.languageData;
    });

    const setLanguage = async (lang: string, reload = false) => {
      if (self.languages.find(l => l.key === lang)) {
        self.language = lang;
        if(!self.languageWPO){
          const lang_mod = lang === 'en' ? 'en_UK' : lang;
          self.languageWPO = lang_mod;
        }
        if (reload) {
          (getParent(self) as any).afterLoad();
        }
        await fetchLanguage();
      }
    };

    const setLanguageWPO =  (lang: string) => {
      self.languageWPO = lang;
    };


    const setLanguageRoot = async (lang: string) => {
      self.languageRoot = lang;
    };

    const setLanguagePhone = async (lang: string) => {
      self.languagePhone = lang;
    };

    const fetchLanguages = flow(function* () {
      self.state = 'pending';
      try {
        const res = yield client.query({ query: GET_LANGUAGES });
        if (res) {
          self.languages = res.data.languages.items;

          const lang = self.languages.find(l => l.key === self.languageRoot);
          if (lang) {
            setLanguage(lang.key);
            setLanguageRoot(lang.key);
          } else {
            setLanguage(DEFAULT_LANGUAGE);
            setLanguageRoot(DEFAULT_LANGUAGE);
          }
        } else {
          setLanguage(DEFAULT_LANGUAGE);
          setLanguageRoot(DEFAULT_LANGUAGE);
        }
        self.state = 'done';
      } catch (error) {
        self.languages = [{ _id: '1', name: DEFAULT_LANGUAGE_LONG, key: DEFAULT_LANGUAGE }];
        setLanguage(DEFAULT_LANGUAGE);
        setLanguageRoot(DEFAULT_LANGUAGE);
        self.state = 'error';
        captureException(error);

      }



      return self.languageData;
    });




    return {
      fetchLanguage,
      fetchLanguages,
      setLanguage,
      setLanguageRoot,
      setLanguageWPO,
      setLanguagePhone,
      afterCreate() {
        fetchLanguages();
        fetchLanguage();
      }
    };
  })
  .views(self => ({
    get translate() {
      try {
        return self.languageData ? JSON.parse(self.languageData) : {};
      } catch (error) {
        captureException(error);
        return {};
      }
    },
    get string() {
      const isSetLang = lang => {
        return lang.key === self.language;
      };
      const setLangObj = self.languages.find(isSetLang);
      if (setLangObj) {
        return setLangObj.name;
      } else {
        return 'Français';
      }
    },
    get name() {
      const isSetLang = lang => {
        return lang.key === self.language;
      };
      const setLangObj = self.languages.find(isSetLang);
      if (setLangObj) {
        return setLangObj;
      } else {
        return { _id: '1', name: 'Français', key: 'fr' };
      }
    },
    get list() {
      return self.languages;
    }
  }));

export type ILanguageStore = Instance<typeof LanguageStore>;
